import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Form, FormItem, InlineNotification, Stack, TextInput } from '@carbon/react';
import DndUploader from 'src/components/Uploader/DNDUploader';
import { useTenantContextValue } from 'src/context/TenantContextProvider';
import { useAuthSession } from 'src/hooks/useAuthSession';
import { useOnboardingStatus } from 'src/hooks/useOnboardingStatus';
import { useMemberships, useUpdateOrganization } from 'src/hooks/useOrganizations';
import { getImageUrl } from 'src/utils/images';

const OrgGeneral = React.memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { context, setContext } = useTenantContextValue();
  const { activeOrganizationId } = useAuthSession();
  const { data: memberships } = useMemberships();
  const { refetch: refetchOnboardingStatus } = useOnboardingStatus();
  const active = useMemo(
    () => memberships?.find((member) => member.id === activeOrganizationId) ?? memberships?.[0],
    [memberships, activeOrganizationId]
  );
  const organizationId = active?.id ?? null;

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [logo, setLogo] = useState('');
  const updateOrganization = useUpdateOrganization();
  const slugChanged = slug !== (active?.slug ?? slug);

  // Seed the form from the active organization whenever it changes, during render
  // rather than in an effect, so the fields never paint blank for a frame first.
  const [seededOrganization, setSeededOrganization] = useState(active);
  if (active && seededOrganization !== active) {
    setSeededOrganization(active);
    setName(active.name);
    setSlug(active.slug);
    setLogo(active.logo ?? '');
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!organizationId) return;
    try {
      await updateOrganization.mutateAsync({ organizationId, name, slug, logo });
      // Onboarding status has a 30s staleTime and drives the context resolver, so the
      // cache can still hold the OLD slug after the context update — refetch it to get
      // the value the resolver will actually see before navigating.
      const { data: fresh } = await refetchOnboardingStatus();
      setContext({ ...context, orgSlug: fresh?.organizationSlug ?? slug });
      navigate('/settings/organization', { replace: true });
    } catch {
      // Error surfaced by the InlineNotification below.
    }
  }

  return (
    <Form onSubmit={onSubmit}>
      {/* Carbon's Form adds no spacing of its own, so without a Stack every field sits
          flush against the next. gap 6 matches RestaurantInformationSettings and the
          other forms in the app. */}
      <Stack gap={6}>
        <TextInput
          id="org-settings-name"
          labelText={t('orgSettings.general.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <TextInput
          id="org-settings-slug"
          labelText={t('orgSettings.general.slug')}
          helperText={t('orgSettings.general.slugHint', { slug })}
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          required
        />
        <FormItem>
          <p className="cds--file--label">{t('orgSettings.general.logo')}</p>
          <DndUploader
            useCropper
            initialValue={active?.logo ? { fileId: active.logo, previewUrl: getImageUrl(active.logo) ?? '' } : null}
            cropperAspectRatio={1}
            previewAspectRatio="1x1"
            onChange={({ fileId }) => setLogo(fileId)}
          />
        </FormItem>
        {slugChanged && (
          <InlineNotification
            kind="warning"
            title={t('orgSettings.general.slugChanged')}
            subtitle={t('orgSettings.general.slugChangedSubtitle', { slug })}
            lowContrast
            hideCloseButton
          />
        )}
        {updateOrganization.error && (
          <InlineNotification
            kind="error"
            title={(updateOrganization.error as Error).message}
            lowContrast
            hideCloseButton
          />
        )}
        <Button type="submit" disabled={updateOrganization.isPending || !organizationId}>
          {t('actions.save')}
        </Button>
      </Stack>
    </Form>
  );
});

export default OrgGeneral;
